import { mockUsers } from "../user/userdata";

/**
 * Mock response for GET /navigation/groups
 *
 * Includes computed fields: section, hierarchyLevel, hasChildren, descendantIds, canHaveSubgroups
 * Groups are pre-sorted: orphanedParents first, then grandparents with descendants nested.
 *
 * hierarchyLevel: 1-indexed hierarchy level (1 = top-level, 2 = child, 3 = grandchild)
 */
// Network ID that returns groups with no subscriptions
export const NO_SUBSCRIBED_GROUPS_NETWORK_ID = 99999;

// Network whose only parent has exactly one child, for the subgroup-delete case where
// removing members can't spill sideways into sibling groups.
export const SINGLE_CHILD_PARENT_NETWORK_ID = 99998;

const mockGroupsRes = (networkId, data, verb, userID) => {
  // Return groups with no subscriptions for testing
  if (networkId === NO_SUBSCRIBED_GROUPS_NETWORK_ID) {
    return {
      success: true,
      groups: [
        {
          ID: 1,
          name: "Unsubscribed Group A",
          shortname: "groupa",
          description: "Test group",
          shortnameSearch: "groupa",
          type: { value: "open", hidden: false, closed: false },
          subscribed: false,
          arran: 1,
          browserIE8orLess: false,
          parentGroupID: 0,
          photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
          includeContacts: false,
          isSmartGroup: false,
          isNew: false,
          canView: true,
          section: "orphanedParents",
          hierarchyLevel: 1,
          hasChildren: false,
          descendantIds: [],
          canHaveSubgroups: true,
        },
        {
          ID: 2,
          name: "Unsubscribed Group B",
          shortname: "groupb",
          description: "Another test group",
          shortnameSearch: "groupb",
          type: { value: "open", hidden: false, closed: false },
          subscribed: false,
          arran: 2,
          browserIE8orLess: false,
          parentGroupID: 0,
          photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
          includeContacts: false,
          isSmartGroup: false,
          isNew: false,
          canView: true,
          section: "orphanedParents",
          hierarchyLevel: 1,
          hasChildren: false,
          descendantIds: [],
          canHaveSubgroups: true,
        },
      ],
    };
  }

  if (networkId === SINGLE_CHILD_PARENT_NETWORK_ID) {
    return {
      success: true,
      groups: [
        {
          ID: 300,
          name: "Lone Parent",
          shortname: "loneparent",
          description: "Parent with a single child",
          shortnameSearch: "loneparent",
          type: { value: "open", hidden: false, closed: false },
          subscribed: true,
          arran: 1,
          browserIE8orLess: false,
          parentGroupID: 0,
          photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
          includeContacts: false,
          isSmartGroup: false,
          isNew: false,
          canView: true,
          section: "grandparents",
          hierarchyLevel: 1,
          hasChildren: true,
          descendantIds: [301],
          canHaveSubgroups: true,
        },
        {
          ID: 301,
          name: "Only Child",
          shortname: "onlychild",
          description: "The parent's only child",
          shortnameSearch: "onlychild",
          type: { value: "open", hidden: false, closed: false },
          subscribed: true,
          arran: 2,
          browserIE8orLess: false,
          parentGroupID: 300,
          photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
          includeContacts: false,
          isSmartGroup: false,
          isNew: false,
          canView: true,
          section: "grandparents",
          hierarchyLevel: 2,
          hasChildren: false,
          descendantIds: [],
          canHaveSubgroups: false,
        },
      ],
    };
  }

  return {
    success: true,
    groups: [
      // === ORPHANED PARENTS SECTION (section: "orphanedParents", hierarchyLevel: 1, parentGroupID: 0) ===
      {
        ID: 8,
        name: "Staff",
        shortname: "staff",
        description: null,
        shortnameSearch: "staff",
        type: {
          value: "hidden",
          hidden: true,
          closed: false,
        },
        subscribed: true,
        arran: 1,
        browserIE8orLess: false,
        parentGroupID: 0,
        photoURL:
          "https://s3-us-west-2.amazonaws.com/dev-photos.flocknote.com/staff/icon.png",
        includeContacts: false,
        isSmartGroup: true,
        isNew: false,
        canView: true,
        // Computed fields
        section: "orphanedParents",
        hierarchyLevel: 1,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: false,
      },
      {
        ID: 9,
        name: "Volunteer Team",
        shortname: "volunteers",
        description: null,
        shortnameSearch: "volunteers",
        type: {
          value: "closed",
          hidden: false,
          closed: true,
        },
        subscribed: true,
        arran: 1,
        browserIE8orLess: false,
        parentGroupID: 0,
        photoURL:
          "https://s3-us-west-2.amazonaws.com/dev-photos.flocknote.com/ministryleaders/icon.png",
        includeContacts: false,
        isSmartGroup: true,
        isNew: false,
        canView: true,
        // Computed fields
        section: "orphanedParents",
        hierarchyLevel: 1,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: false,
      },
      {
        ID: 10,
        name: "Youth Ministry",
        shortname: "youth",
        description:
          "High school youth and families are invited to join our Life Teen program, where we deepen our faith in Jesus Christ and His Church through engaging experiences. Life Nights are every Sunday after 6pm Mass (7-8:30pm). Parents of interested teens or any adults interested in Youth Ministry—join this group and a Core Member will contact you with details.",
        shortnameSearch: "youth",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: true,
        arran: 1,
        browserIE8orLess: false,
        parentGroupID: 0,
        photoURL:
          "https://s3-us-west-2.amazonaws.com/dev-photos.flocknote.com/pastoralcouncil/icon.png",
        includeContacts: false,
        isSmartGroup: true,
        isNew: false,
        canView: true,
        // Computed fields
        section: "orphanedParents",
        hierarchyLevel: 1,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: false,
      },
      {
        ID: 11,
        name: "Bible Study Group",
        shortname: "biblestudy",
        description: "test",
        shortnameSearch: "biblestudy",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: false,
        arran: 4,
        browserIE8orLess: false,
        parentGroupID: 0,
        photoURL:
          "https://s3-us-west-2.amazonaws.com/dev-photos.flocknote.com/altarservers/icon.png",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        // Computed fields
        section: "orphanedParents",
        hierarchyLevel: 1,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: false,
      },
      {
        ID: 176,
        name: "Testing",
        shortname: "testing",
        description: "",
        shortnameSearch: "testing",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: true,
        arran: 6,
        browserIE8orLess: false,
        parentGroupID: 0,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        // Computed fields
        section: "orphanedParents",
        hierarchyLevel: 1,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: false,
      },
      {
        ID: 177,
        name: "test",
        shortname: "test3",
        description: "",
        shortnameSearch: "test3",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: true,
        arran: 1,
        browserIE8orLess: false,
        parentGroupID: 0,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: true,
        isNew: false,
        canView: true,
        // Computed fields
        section: "orphanedParents",
        hierarchyLevel: 1,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: false,
      },

      // === GRANDPARENTS SECTION (section: "grandparents", hierarchyLevel: 1, parentGroupID: 0) ===
      {
        ID: 12,
        name: "Super's Test Group",
        shortname: "test",
        description: null,
        shortnameSearch: "test",
        type: {
          value: "hidden",
          hidden: true,
          closed: false,
        },
        subscribed: true,
        arran: 5,
        browserIE8orLess: false,
        parentGroupID: 0,
        photoURL:
          "https://s3-us-west-2.amazonaws.com/dev-photos.flocknote.com/test/icon.png",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 1,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: true,
      },
      // Parent with only unsubscribed children (for testing expand button visibility)
      {
        ID: 200,
        name: "Parent With Hidden Children",
        shortname: "parenthidden",
        description: "A subscribed parent whose children are all unsubscribed",
        shortnameSearch: "parenthidden",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: true,
        arran: 7,
        browserIE8orLess: false,
        parentGroupID: 0,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 1,
        hasChildren: true,
        descendantIds: [201, 202],
        canHaveSubgroups: true,
      },
      // Unsubscribed children of Parent With Hidden Children (ID: 200)
      {
        ID: 201,
        name: "Hidden Child One",
        shortname: "hiddenchildone",
        description: "",
        shortnameSearch: "hiddenchildone",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: false,
        arran: 1,
        browserIE8orLess: false,
        parentGroupID: 200,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 2,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: true,
      },
      {
        ID: 202,
        name: "Hidden Child Two",
        shortname: "hiddenchildtwo",
        description: "",
        shortnameSearch: "hiddenchildtwo",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: false,
        arran: 2,
        browserIE8orLess: false,
        parentGroupID: 200,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 2,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: true,
      },
      {
        ID: 178,
        name: "Individuals",
        shortname: "individuals",
        description: "",
        shortnameSearch: "individuals",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: true,
        arran: 1,
        browserIE8orLess: false,
        parentGroupID: 0,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        isGroupAdmin: userID === mockUsers["Lonely Pilgrim"] ? true : false,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 1,
        hasChildren: true,
        descendantIds: [63, 60, 61, 179, 59, 62],
        canHaveSubgroups: true,
      },
      // Children of Individuals (ID: 178)
      {
        ID: 63,
        name: "Hi Austin",
        shortname: "hiaustin",
        description: "Testing a smart group",
        shortnameSearch: "hiaustin",
        type: {
          value: "closed",
          hidden: false,
          closed: true,
        },
        subscribed: true,
        arran: 1,
        browserIE8orLess: false,
        parentGroupID: 178,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: true,
        isNew: false,
        canView: true,
        isGroupAdmin: userID === mockUsers["Lonely Pilgrim"] ? true : false,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 2,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: true,
      },
      {
        ID: 60,
        name: "Litmus",
        shortname: "Litmus",
        description: "",
        shortnameSearch: "litmus",
        type: {
          value: "closed",
          hidden: false,
          closed: true,
        },
        subscribed: false,
        arran: 10,
        browserIE8orLess: false,
        parentGroupID: 178,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        isGroupAdmin: userID === mockUsers["Lonely Pilgrim"] ? true : false,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 2,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: true,
      },
      {
        ID: 61,
        name: "Just Anthony",
        shortname: "JustAnthony",
        description: "",
        shortnameSearch: "justanthony",
        type: {
          value: "closed",
          hidden: false,
          closed: true,
        },
        subscribed: false,
        arran: 9,
        browserIE8orLess: false,
        parentGroupID: 178,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        isGroupAdmin: userID === mockUsers["Lonely Pilgrim"] ? true : false,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 2,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: true,
      },
      {
        ID: 179,
        name: "Caleb",
        shortname: "caleb",
        description: "",
        shortnameSearch: "caleb",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: true,
        arran: 12,
        browserIE8orLess: false,
        parentGroupID: 178,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        isGroupAdmin: userID === mockUsers["Lonely Pilgrim"] ? true : false,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 2,
        hasChildren: true,
        descendantIds: [59, 62],
        canHaveSubgroups: true,
      },
      // Grandchildren (children of Caleb, ID: 179)
      {
        ID: 59,
        name: "Just Caleb",
        shortname: "JustCaleb",
        description: "",
        shortnameSearch: "justcaleb",
        type: {
          value: "closed",
          hidden: false,
          closed: true,
        },
        subscribed: false,
        arran: 13,
        browserIE8orLess: false,
        parentGroupID: 179,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        isGroupAdmin: userID === mockUsers["Lonely Pilgrim"] ? true : false,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 3,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: false,
      },
      {
        ID: 62,
        name: "Just Gmail",
        shortname: "JustGmail",
        description: "",
        shortnameSearch: "justgmail",
        type: {
          value: "open",
          hidden: false,
          closed: false,
        },
        subscribed: true,
        arran: 14,
        browserIE8orLess: false,
        parentGroupID: 179,
        photoURL: "https://dhdj1c2suf90g.cloudfront.net/images/nophoto.jpg",
        includeContacts: false,
        isSmartGroup: false,
        isNew: false,
        canView: true,
        isGroupAdmin: userID === mockUsers["Lonely Pilgrim"] ? true : false,
        // Computed fields
        section: "grandparents",
        hierarchyLevel: 3,
        hasChildren: false,
        descendantIds: [],
        canHaveSubgroups: false,
      },
    ],
  };
};

/**
 * Static version of mock data for simpler test cases.
 */
export const mockGroupsStatic = mockGroupsRes(null, null, null, null);

/**
 * Returns the mock groups data.
 * In test environment, returns the static mock for consistent assertions.
 */
export const getMockGroups = () => {
  return mockGroupsStatic;
};

export default mockGroupsRes;
